// ══════════════════════════════════════════════════════════════════════════════════════════════
//  t360 (21-oct-2026) · «LOS WHATSAPP DE LA CLÍNICA NO SALEN» — EL AVISO FIJO DE ARRIBA
// ══════════════════════════════════════════════════════════════════════════════════════════════
//  Cuando Meta no deja enviar por la CUENTA de la clínica (131042 el pago, 131031 y 368 un bloqueo, 131048 el límite del
//  número), cada mensaje salía «No entregado» en inglés y nadie sabía que eran TODOS ni dónde se arreglaba. Esta franja,
//  entre la cabecera y el contenido (como components/AvisoCitasPorArreglar), lo dice en llano mientras siga pasando y se va
//  sola cuando un envío vuelve a salir bien (lo decide el servidor: backend/services/erroresCuentaMeta · estado).
//  Lo ve quien tiene Comunicación y la administración. Con el botón «Reenviar los que fallaron por esto» se manda otra vez,
//  UNA vez, cada mensaje de los últimos 3 días que no salió por eso; lo que no se puede (citas pasadas, textos fuera de las
//  24 horas…) se dice, y la publicidad (campañas y plantillas de marketing) no se reenvía: junto al botón dice cuántos. Cuando el aviso ya se ha ido y quedan mensajes sin reenviar, sale una franja más suave con el mismo
//  botón (se puede quitar con «Ahora no» hasta que cambie el número). En una visita del soporte no se ofrece el botón.
import { useState } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import api, { enVisitaDelSoporte } from '../services/api'
import { usePermisos } from '../hooks/usePermisos'
import { Ico } from './ui'
import { t, tx } from '../utils/i18n'   // B-2259 · los textos, en el idioma de quien mira (utils/i18n)

const CLAVE_OCULTO = 'biango:cuenta-meta-oculto'
const leerOculto = () => { try { return sessionStorage.getItem(CLAVE_OCULTO) || '' } catch { return '' } }
const guardarOculto = (v) => { try { sessionStorage.setItem(CLAVE_OCULTO, String(v)) } catch { /* modo privado */ } }

// El coordinador (21-oct-2026): la publicidad (campañas y plantillas de marketing) no se reenvía, y el botón dice cuántos.
export const textoPublicidad = (n) => (!n ? '' : t('components.AvisoCuentaMeta.plural_mensajes_de_publicidad_no', { one: '{{count}} mensaje de publicidad no se reenvía (campañas y plantillas de marketing).', other: '{{count}} mensajes de publicidad no se reenvían (campañas y plantillas de marketing).' }, { count: n, _forma: (n === 1) ? 'one' : 'other' }))

export const textoPendientes = (n) => (t('components.AvisoCuentaMeta.plural_mensajes_de_los_ultimos', { one: '{{count}} mensaje de los últimos 3 días no salió por un problema con el envío de WhatsApp.', other: '{{count}} mensajes de los últimos 3 días no salieron por un problema con el envío de WhatsApp.' }, { count: n, _forma: (n === 1) ? 'one' : 'other' }))

// El resumen de lo que ha hecho el botón, en llano.
export function textoResultado(r) {
  if (!r) return ''
  const partes = []
  if (r.reenviados) partes.push(t('components.AvisoCuentaMeta.plural_se_han_reenviado_mensajes', { one: 'Se ha reenviado {{count}} mensaje.', other: 'Se han reenviado {{count}} mensajes.' }, { count: r.reenviados, _forma: (r.reenviados === 1) ? 'one' : 'other' }))
  else if (!r.sigue_fallando && !r.parado) partes.push(t('components.AvisoCuentaMeta.no_se_ha_reenviado_ningun', 'No se ha reenviado ningún mensaje.'))
  if (r.sigue_fallando) partes.push(r.error_meta || t('components.AvisoCuentaMeta.todavia_no_se_pueden_enviar', 'Todavía no se pueden enviar: el problema sigue. Vuelve a pulsar cuando se resuelva.'))
  if (r.parado) partes.push(t('components.AvisoCuentaMeta.whatsapp_esta_parado_en_la', 'WhatsApp está parado en la clínica: {{parado}}.', { parado: r.parado }))
  if (r.quedan) partes.push(t('components.AvisoCuentaMeta.plural_quedan_por_reenviar_vuelve', { one: 'Queda {{count}} por reenviar: vuelve a pulsar.', other: 'Quedan {{count}} por reenviar: vuelve a pulsar.' }, { count: r.quedan, _forma: (r.quedan === 1) ? 'one' : 'other' }))
  return partes.join(' ')
}

export default function AvisoCuentaMeta() {
  const { permiso, permisosListos, esAdmin } = usePermisos()
  const qc = useQueryClient()
  const puede = !!permisosListos && (esAdmin || permiso('conversaciones'))
  const [enviando, setEnviando] = useState(false)
  const [resultado, setResultado] = useState(null)
  const [error, setError] = useState('')
  const [oculto, setOculto] = useState(leerOculto)
  const { data } = useQuery({
    queryKey: ['cuenta-meta-aviso'],
    queryFn: () => api.get('/conversaciones/errores-envio/cuenta-meta').then(r => r.data),
    enabled: puede,
    refetchInterval: 60000,
    staleTime: 30000,
    retry: false,
  })
  if (!puede || !data) return null
  const n = Number(data.pendientes) || 0
  const pub = Number(data.publicidad) || 0
  const activo = data.activo === true
  const visita = enVisitaDelSoporte()
  const conBoton = data.puede_reenviar === true && n > 0 && !visita
  if (!activo && !resultado && (!conBoton || oculto === String(n))) return null

  const reenviar = async () => {
    if (enviando) return
    setEnviando(true); setError('')
    try {
      const r = await api.post('/conversaciones/errores-envio/cuenta-meta/reenviar')
      setResultado(r.data)
    } catch (e) {
      setError(e.response?.data?.error || t('components.AvisoCuentaMeta.no_se_han_podido_reenviar', 'No se han podido reenviar. Vuelve a intentarlo en unos minutos.'))
    } finally {
      setEnviando(false)
      qc.invalidateQueries({ queryKey: ['cuenta-meta-aviso'] })
    }
  }
  const color = activo ? 'var(--danger, #dc2626)' : 'var(--info, #2563eb)'
  return (
    <div role={activo ? 'alert' : 'status'} data-aviso-cuenta-meta={activo ? data.motivo || 'activo' : 'pendientes'}
      style={{ display: 'flex', alignItems: 'flex-start', gap: 10, flexWrap: 'wrap', padding: '8px max(16px, env(safe-area-inset-right)) 8px max(16px, env(safe-area-inset-left))',
        background: `color-mix(in srgb, ${color} 12%, var(--surface))`, borderBottom: `1px solid color-mix(in srgb, ${color} 35%, var(--border))`,
        color: 'var(--text-primary)', fontSize: 13.5, lineHeight: 1.4, flexShrink: 0 }}>
      <span style={{ display: 'inline-flex', paddingTop: 2 }}><Ico n="warn" s={16} c={color} /></span>
      <div style={{ flex: '1 1 260px', minWidth: 0 }}>
        {activo ? (
          <>
            <div style={{ fontWeight: 800 }}>{data.titulo}</div>
            <div style={{ color: 'var(--text-secondary)' }}>
              {data.texto}{data.desde_hora ? t('components.AvisoCuentaMeta.ultimo_fallo_a_las', ' Último fallo a las {{desde_hora}}.', { desde_hora: data.desde_hora }) : ''}
            </div>
          </>
        ) : n > 0 ? (
          <div style={{ fontWeight: 700 }}>{tx('components.AvisoCuentaMeta.si_ya_se_ha_resuelto', '{{textoPendientes}} Si ya se ha resuelto, puedes reenviarlos.', { textoPendientes: textoPendientes(n) })}</div>
        ) : null}
        {resultado && (
          <div data-aviso-cuenta-meta-resultado="" style={{ marginTop: 4, color: 'var(--text-secondary)' }}>
            <div style={{ fontWeight: 700, color: 'var(--text-primary)' }}>{textoResultado(resultado)}</div>
            {Array.isArray(resultado.explicacion) && resultado.explicacion.length > 0 && (
              <ul style={{ margin: '4px 0 0', paddingLeft: 18 }}>
                {resultado.explicacion.map(x => (
                  <li key={x.motivo}>{tx('components.AvisoCuentaMeta.no_reenviados', 'No reenviados ({{n}}): {{texto}}.', { n: x.n, texto: x.texto })}</li>
                ))}
              </ul>
            )}
          </div>
        )}
        {error && <div role="alert" style={{ marginTop: 4, color: 'var(--danger, #dc2626)', fontWeight: 700 }}>{error}</div>}
      </div>
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
        {conBoton && (
          <button type="button" className="btn btn-primary" data-aviso-cuenta-meta-reenviar="" onClick={reenviar} disabled={enviando}
            style={{ minHeight: 32, whiteSpace: 'nowrap' }}>
            {enviando ? t('components.AvisoCuentaMeta.reenviando', 'Reenviando…') : t('components.AvisoCuentaMeta.reenviar_los_que_fallaron_por', 'Reenviar los que fallaron por esto ({{n}})', { n })}
          </button>
        )}
        {pub > 0 && (conBoton || activo) && (
          <span data-aviso-cuenta-meta-publicidad={pub} style={{ fontSize: 12, color: 'var(--text-secondary)', maxWidth: 260 }}>{textoPublicidad(pub)}</span>
        )}
        {!activo && (
          <button type="button" className="btn" data-aviso-cuenta-meta-ocultar=""
            onClick={() => { guardarOculto(n); setOculto(String(n)); setResultado(null); setError('') }}
            style={{ minHeight: 32, whiteSpace: 'nowrap' }}>
            {resultado ? t('components.AvisoCuentaMeta.cerrar', 'Cerrar') : t('components.AvisoCuentaMeta.ahora_no', 'Ahora no')}
          </button>
        )}
      </div>
    </div>
  )
}
